import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { BlogIndexView } from 'alakai-aloha-shared/public-ui';
import {
  islandApplicableDocumentFilter,
  islandScopedDocumentFilter,
  sanityFetch,
} from '../../lib/sanity';
import { urlFor } from '../../lib/sanityImage';
import { buildRuntimeCanonicalUrl } from '../../lib/siteUrls';
import { getRuntimeSiteContext } from '../../lib/siteContext';

const CATEGORY_ORDER = [
  'Trip Planning & Travel Tips',
  'Wildlife',
  'Nature, Hiking & Scenic Adventures',
  'Culture, History & Language',
  'Festivals & Events',
  'Family Travel',
  'Food & Dining',
];

function compareCategories(a, b) {
  const aIndex = CATEGORY_ORDER.indexOf(a.title);
  const bIndex = CATEGORY_ORDER.indexOf(b.title);

  if (aIndex === -1 && bIndex === -1) {
    return a.title.localeCompare(b.title);
  }

  if (aIndex === -1) {
    return 1;
  }

  if (bIndex === -1) {
    return -1;
  }

  return aIndex - bIndex;
}

function mapCardPost(post) {
  return {
    id: post._id,
    title: post.title,
    slug: post.slug?.current || post.slug,
    excerpt: post.excerpt,
    imageUrl: post.mainImage?.asset
      ? urlFor(post.mainImage).width(400).height(300).auto('format').quality(70).url()
      : undefined,
    imageAlt: post.mainImage?.alt || post.title,
  };
}

function portableTextToPlainText(body = []) {
  return body
    .map((block) => {
      if (block?._type === 'block' && Array.isArray(block.children)) {
        return block.children.map((child) => child.text || '').join(' ');
      }
      return '';
    })
    .join(' ');
}

const BlogIndex = () => {
  const { islandConfig } = getRuntimeSiteContext();
  const [categories, setCategories] = useState([]);
  const [searchPosts, setSearchPosts] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const categoryQuery = `*[_type == "category" && ${islandApplicableDocumentFilter()}] | order(title asc) {
        _id,
        title,
        "slug": slug.current,
        description
      }`;
      const postQuery = `*[_type == "blogPost" && ${islandScopedDocumentFilter()}] {
        _id,
        title,
        slug,
        excerpt,
        publishedAt,
        mainImage {
          asset->{ url },
          alt
        },
        category->{
          _id,
          title,
          "slug": slug.current
        }
      }`;
      const searchQuery = `*[_type == "blogPost" && ${islandScopedDocumentFilter()}] {
        _id,
        title,
        slug,
        excerpt,
        publishedAt,
        mainImage {
          asset->{ url },
          alt
        },
        body
      }`;

      const [categoryResults, posts, searchResults] = await Promise.all([
        sanityFetch(categoryQuery),
        sanityFetch(postQuery),
        sanityFetch(searchQuery),
      ]);

      const orderedCategories = categoryResults
        .slice()
        .sort(compareCategories)
        .map((category) => ({
          id: category._id,
          title: category.title,
          slug: category.slug,
          description: category.description,
          posts: posts
            .filter((post) => post.category?._id === category._id)
            .sort(() => 0.5 - Math.random())
            .map(mapCardPost),
        }))
        .filter((category) => category.posts.length > 0);

      setCategories(orderedCategories);
      setSearchPosts(
        searchResults.map((post) => ({
          ...mapCardPost(post),
          searchText: portableTextToPlainText(post.body),
        })),
      );
    };

    fetchData();
  }, []);

  return (
    <>
      <Helmet>
        <title>{`${islandConfig.title} Travel Blog | Alaka'i Aloha`}</title>
        <meta
          name="description"
          content={`${islandConfig.title} travel guides, tips, itineraries, and local insights from Alaka'i Aloha.`}
        />
        <link rel="canonical" href={buildRuntimeCanonicalUrl('/blog')} />
      </Helmet>
      <BlogIndexView
        categories={categories}
        searchPosts={searchPosts}
        onSelectCategory={(slug) => {
          window.location.href = `/blog/category/${slug}`;
        }}
      />
    </>
  );
};

export default BlogIndex;
